import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { SourceEntity } from '../../../source/source-entity';
import {
  ISO_DATE_TIME_FORMAT,
  Timestamp,
  Timezone,
  toTimestamp,
  TypeGuard,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { cleanObjectStrings } from './lib/conversion-helpers';
import * as moment from 'moment-timezone';

export const PATIENT_HISTORY_NOTE_RESOURCE_TYPE = 'patientHistoryNote';

export const PATIENT_HISTORY_NOTE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient History Note List',
    description: '',
    idPrefix: PATIENT_HISTORY_NOTE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

const PATIENT_HISTORY_NOTE_QUERY = `
SELECT
  patients_history_id AS id,
  patient_id,
  NULLIF(medical_history, '') AS medical_history_note,
  NULLIF(dental_history, '') AS dental_history_note,
  NULLIF(user_id, '') AS user_id,
  date_created AS created_at
FROM patients_history
WHERE medical_history != '' OR dental_history != ''
`;

export interface ID4WPatientHistoryNote {
  id: number;
  patient_id: number;
  medical_history_note?: string | null;
  dental_history_note?: string | null;
  user_id?: string | null;
  created_at: string; // ISODateTimeType
}

export interface ID4WPatientHistoryNoteTranslations {
  createdAt: Timestamp;
}

export interface ID4WPatientHistoryNoteFilters {
  patientId: string;
}

export function isD4WPatientHistoryNote(
  item: unknown,
  logError: boolean
): item is ID4WPatientHistoryNote {
  return TypeGuard.interface<ID4WPatientHistoryNote>(
    {
      id: isNumber,
      patient_id: isNumber,
      medical_history_note: TypeGuard.nilOr(isString),
      dental_history_note: TypeGuard.nilOr(isString),
      user_id: TypeGuard.nilOr(isString),
      created_at: isString,
    },
    logError
  )(item);
}

export type D4WPatientHistoryNote = ISourceEntityTypes<
  ID4WPatientHistoryNote,
  ID4WPatientHistoryNoteTranslations,
  ID4WPatientHistoryNoteFilters
>;

export class PatientHistoryNoteSourceEntity extends BaseSourceEntity<D4WPatientHistoryNote> {
  sourceEntity = PATIENT_HISTORY_NOTE_SOURCE_ENTITY;
  entityResourceType = PATIENT_HISTORY_NOTE_RESOURCE_TYPE;
  sourceQuery = PATIENT_HISTORY_NOTE_QUERY;
  verifySourceFn = isD4WPatientHistoryNote;
  override defaultOffsetSize = 10000;

  override transformDataFn = flow([
    (rows: ID4WPatientHistoryNote[]) => rows.map(cleanObjectStrings),
  ]);

  translate(
    data: ID4WPatientHistoryNote,
    timezone: Timezone
  ): ID4WPatientHistoryNoteTranslations {
    return {
      createdAt: toTimestamp(
        moment.tz(data.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }

  getSourceRecordId(data: ID4WPatientHistoryNote): string {
    return `${data.patient_id}-${data.id}`;
  }

  getSourceLabel(data: ID4WPatientHistoryNote): string {
    return `${data.patient_id}-${data.id}`;
  }

  getFilterData(data: ID4WPatientHistoryNote): ID4WPatientHistoryNoteFilters {
    return {
      patientId: String(data.patient_id),
    };
  }
}
